// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { DashboardRowOutput, WidgetOutput } from 'thehive-sdk';
import { WidgetView } from '@Organisms/Dashboard/DashboardDetails/WidgetView/WidgetView';
import { useDashboard } from '@Hooks/useDashboard';
import DropableWidgetViewList from '@Organisms/Dashboard/DashboardDetails/DropableWidgetViewList/DropableWidgetViewList';
import { addElementOnArray, deleteElementOnArray, moveElementOnArray, useLocalMessage } from '@Helpers/';
import { DashboardDropWidgetResult } from '@Types';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ICONS } from '@Constants/iconsApp';

import './WidgetRow.scss';

interface WidgetRowProps {
    row: DashboardRowOutput;
    rowIndex: number;
}

const WidgetRow = ({ row, rowIndex }: WidgetRowProps): ReactElement => {
    const { items: widgetList } = row;

    const i = useLocalMessage();

    const { dashboard, editMode, setRows, setRow, widgets = [] } = useDashboard();

    const {
        definition: { items: rows },
    } = dashboard;

    // edit the dashboard when sorting is finished
    const onDragFinish = (
        dropResult: DashboardDropWidgetResult,
        originWidgetIndex: number,
        widgetData: WidgetOutput,
    ): void => {
        const { hoveredRowIndex, hoveredWidgetIndex } = dropResult || {};
        if (!hoveredRowIndex && hoveredRowIndex !== 0) {
            return;
        }

        if (hoveredRowIndex === rowIndex) {
            setTimeout(() => {
                setRow(
                    {
                        ...row,
                        items: moveElementOnArray(widgetList, originWidgetIndex, hoveredWidgetIndex),
                    },
                    rowIndex,
                );
            });
        } else {
            const newRows = rows.map((container: DashboardRowOutput, currentRowIndex: number): DashboardRowOutput => {
                const items = container.items;
                if (currentRowIndex === hoveredRowIndex) {
                    return {
                        ...container,
                        items: addElementOnArray(items, widgetData, hoveredWidgetIndex),
                    };
                } else if (currentRowIndex === rowIndex) {
                    return {
                        ...container,
                        items: deleteElementOnArray(items, originWidgetIndex),
                    };
                } else {
                    return container;
                }
            });
            setTimeout(() => {
                setRows(newRows);
            });
        }
    };

    const deleteRow = (): void => {
        setRows(rows.filter((_, index) => index !== rowIndex));
    };

    const canAddWidget = widgetList.length < 3;

    const list = useMemo(
        () =>
            widgetList.map((widget) => {
                const widgetQuery = widgets.find(({ widget: { id } }) => id === widget.id);

                return (
                    <WidgetView
                        className="widget-row__view"
                        widget={widget}
                        key={`div-item-${widget.id}`}
                        query={widgetQuery?.query}
                    />
                );
            }),
        [widgetList, widgets],
    );

    return (
        <div className="widget-row">
            {editMode ? (
                <div className="widget-row__editable">
                    <div className="widget-row__delete">
                        {widgetList.length > 0 ? (
                            <THPopConfirm
                                title={i('app.dashboard.widget.row.delete.modal.content')}
                                onConfirm={deleteRow}
                            >
                                <THButton danger icon={<ICONS.DeleteIcon />} size="small" />
                            </THPopConfirm>
                        ) : (
                            <THButton danger icon={<ICONS.DeleteIcon />} size="small" onClick={deleteRow} />
                        )}
                    </div>
                    <DropableWidgetViewList
                        className="widget-row__view"
                        rowIndex={rowIndex}
                        canAddWidget={canAddWidget}
                        widgetList={widgetList}
                        onDragFinish={onDragFinish}
                        widgets={widgets}
                    />
                </div>
            ) : (
                list
            )}
        </div>
    );
};

export default WidgetRow;
